import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getPatientClient, getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { HttpError, type ComputedSlot } from '@vagusx/clients';
import type { BookingPatientInfo } from '../types';

// Backend code (ERROR_CODES.OPERATION_NOT_ALLOWED) for "the EHR genuinely can't
// write this patient" — no EHR integration, connector unsupported (Office Ally),
// or the location isn't EHR-mapped. Kept as a literal because @vagusx/shared's
// ERROR_CODES isn't exported to the web layer (mirrors HttpError.isDuplicate,
// which also matches code strings directly).
const EHR_WRITE_UNSUPPORTED_CODE = 'OPERATION_NOT_ALLOWED';

/**
 * Create the new booking patient — in the EHR first (system of record), falling
 * back to a local-only patient ONLY when the EHR genuinely can't write this
 * patient (no EHR integration, connector unsupported like Office Ally, or the
 * location isn't EHR-mapped) — all surfaced as OPERATION_NOT_ALLOWED. Every other
 * failure (transient 503, duplicate, a real validation rejection) propagates, so a
 * genuine EHR failure is never silently turned into an unlinked local patient
 * (ADR-089). Returns the new patient id.
 */
async function createBookingPatient(info: BookingPatientInfo, locationId: string): Promise<string> {
  try {
    const patient = await getPatientClient().createInEhr({
      firstName: info.firstName,
      lastName: info.lastName,
      dateOfBirth: info.dateOfBirth,
      phone: info.phone,
      locationId,
    });
    return patient.id;
  } catch (err) {
    if (!(err instanceof HttpError) || err.code !== EHR_WRITE_UNSUPPORTED_CODE) throw err;
  }

  const local = await getPatientClient().create({
    firstName: info.firstName,
    lastName: info.lastName,
    dateOfBirth: info.dateOfBirth,
    phone: info.phone,
  });
  return local.id;
}

interface BookingData {
  slot: ComputedSlot;
  patientId?: string;
  newPatient?: BookingPatientInfo;
  reasonForVisit?: string;
  bookedVia?: 'web' | 'staff';
}

interface UseBookingResult {
  isBooking: boolean;
  book: (data: BookingData) => Promise<boolean>;
}

/**
 * Hook that orchestrates the booking flow:
 * 1. If new patient → create patient first
 * 2. Book appointment with the patient ID
 */
export function useBooking(): UseBookingResult {
  const queryClient = useQueryClient();
  const [isBooking, setIsBooking] = useState(false);

  const book = async (data: BookingData): Promise<boolean> => {
    setIsBooking(true);

    try {
      let patientId = data.patientId;

      // Step 1: Create patient if needed (EHR-first, ADR-089)
      if (!patientId && data.newPatient) {
        try {
          patientId = await createBookingPatient(data.newPatient, data.slot.locationId);
        } catch (err: unknown) {
          // Check for duplicate patient
          // POSSIBLE_DUPLICATE: the EHR has someone who may be this patient. This
          // path cannot show the candidates, so send the user to search instead.
          if (
            (err instanceof HttpError && (err.isDuplicate || err.code === 'POSSIBLE_DUPLICATE')) ||
            (err instanceof Error && err.message.includes('duplicate'))
          ) {
            toast.error(
              'A patient with this information already exists. Please search and select them.'
            );
            return false;
          }
          throw err;
        }
      }

      if (!patientId) {
        toast.error('Please select or create a patient');
        return false;
      }

      // Step 2: Book the appointment
      try {
        await getAppointmentClient().book({
          patientId,
          providerId: data.slot.providerId,
          locationId: data.slot.locationId,
          startTime: data.slot.startTime,
          durationMinutes: data.slot.durationMinutes,
          // Forward the appointment type so the EHR write has it — without it the
          // sync defers to PENDING. Omit when empty (the schema expects a uuid).
          appointmentTypeId: data.slot.appointmentTypeId || undefined,
          reasonForVisit: data.reasonForVisit,
          bookedVia: data.bookedVia ?? 'web',
        });

        toast.success('Appointment booked successfully');
        queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
        return true;
      } catch (err: unknown) {
        // Surface the actual booking violation(s) (eligibility, business hours,
        // referral, double-booking, …) instead of a generic "try again". The
        // API returns them as fieldErrors on a 422 validation error; show every
        // distinct message so a multi-violation response isn't reduced to one.
        if (err instanceof HttpError && err.isValidation && err.fieldErrors.length > 0) {
          const messages = [...new Set(err.fieldErrors.map((e) => e.message).filter(Boolean))];
          toast.error(messages.join('\n') || 'This booking is not allowed.');
        } else if (
          (err instanceof HttpError && err.isConflict) ||
          (err instanceof Error && err.message.includes('conflict'))
        ) {
          toast.error('This slot is no longer available. Please select a different time.');
        } else if (bookingRefusal(err)) {
          // The EHR refused the booking (the time was taken, or it can't be booked):
          // say why, and refresh the offered slots so the refused time drops out.
          toast.error(bookingRefusal(err));
          queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
        } else {
          throw err;
        }
        return false;
      }
    } catch (err) {
      toast.error('Failed to book appointment. Please try again.');
      return false;
    } finally {
      setIsBooking(false);
    }
  };

  return { isBooking, book };
}

/**
 * The message for a booking the API refused (400 `BOOKING_FAILED`: the EHR's reason,
 * e.g. "That time is no longer available."), or null for any other error.
 */
export function bookingRefusal(err: unknown): string | null {
  if (!(err instanceof HttpError) || err.code !== 'BOOKING_FAILED') return null;
  return err.message || 'The booking was refused. Please select a different time.';
}
